import { useQuery } from '@apollo/client';
import { Link } from 'react-router';

import Button from '@/componentsv2/Button';
import { ERROR_CODES, hasErrorCode } from '@/core/api';

import { formatDateShort } from '../components/EngagementHub/format';
import { useConsultingEngagementsEnabled } from '../featureFlag';
import StatusBadge from './StatusBadge';
import { formatNteHours } from './money';
import { ENGAGEMENTS_BY_PROJECT } from './operations';
import { type ProjectEngagementRow, fromProjectEngagements } from './viewModel';

interface ProjectEngagementsSectionProps {
  projectId: string;
}

const PAGE_SIZE = 20;

/**
 * The project page's engagements list.
 *
 * **Section-append, not a tab.** `ProjectDetails` is a v1 page (`connect` +
 * `redux-form` + scss, single scroll, no tab framework), so hosting a
 * `componentsv2/Tabs` there would mean restructuring the whole page. This
 * component is v2; its host stays as it is.
 *
 * **Read-only, and FE6 did not change that.** The live `HireExpertDrawer` needs
 * an expert source (`consultationId | candidateId | profileId | expertId`), and
 * a bare project CTA has none, so an expert-picker step is unspecced new design.
 *
 * `customer_introduced` looks like the way around that — it is the one creation
 * path that takes no source ref and may open expertless — but it is a trap here.
 * There is **no mutation that attaches an expert to an existing engagement**:
 * `makeEngagementOffer` is the expert's own move on an engagement that already
 * names them, and nothing else writes `engagements.expert_id`. An expertless
 * engagement created from this button would therefore sit at `pending_offer`
 * forever, with no expert able to see it and no way to add one. The api's own
 * words for that state are "a bare placeholder until the expert is attached"
 * (`create.go`), and the attaching half is not built.
 *
 * So the CTA waits on either an expert picker or an attach-expert mutation. The
 * candidate-card and profile entries carry a source natively and are the cheaper
 * first mounts.
 */
export default function ProjectEngagementsSection({
  projectId,
}: ProjectEngagementsSectionProps): JSX.Element | null {
  // Flag off ⇒ the section is absent, and no query is issued: the gate wraps
  // the component rather than living inside it, so nothing below runs at all.
  if (!useConsultingEngagementsEnabled()) return null;
  return <ProjectEngagements projectId={projectId} />;
}

/**
 * The section proper, ungated.
 *
 * Exported so Storybook can render it: the gate reads the session, which
 * Storybook has no meaningful version of, and a story that renders `null` shows
 * a reviewer nothing.
 */
export function ProjectEngagements({
  projectId,
}: ProjectEngagementsSectionProps): JSX.Element | null {
  const { data, loading, error, refetch } = useQuery(ENGAGEMENTS_BY_PROJECT, {
    variables: { projectId, first: PAGE_SIZE },
  });

  if (loading && !data) return null;

  /**
   * Decision 5: `engagementsByProject` authorizes per page, all-or-nothing, and
   * **denies as an error rather than as an empty page**. A permission-shaped
   * error therefore means "not yours to see" — render nothing, exactly as if the
   * feature were off, rather than an error box on a page the viewer legitimately
   * has open. Anything else is transient and gets a retry.
   */
  if (error) {
    const denied =
      hasErrorCode(error, ERROR_CODES.PERMISSION_NOT_ALLOWED) ||
      hasErrorCode(error, ERROR_CODES.ENGAGEMENT_NOT_FOUND);
    if (denied) return null;
    return (
      <Section>
        <p className="text-on-surface-muted body-2">We couldn’t load this project’s engagements.</p>
        <div className="mt-12">
          <Button size="small" variant="secondary" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      </Section>
    );
  }

  const rows = fromProjectEngagements(
    data?.engagementsByProject ?? { edges: [], pageInfo: { hasNextPage: false } }
  );

  return (
    <Section>
      {rows.length === 0 ? (
        <p className="text-on-surface-muted body-2">
          No engagements on this project yet. An engagement starts from an expert’s profile or a
          candidate on an expert request.
        </p>
      ) : (
        <ul className="flex list-none flex-col gap-12 p-[0]">
          {rows.map((row) => (
            <li key={row.engagementId}>
              <Row row={row} />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function Section({ children }: { children: React.ReactNode }) {
  return (
    <section className="mt-24 flex flex-col gap-12">
      <h2 className="text-on-surface hd-5">Engagements</h2>
      {children}
    </section>
  );
}

function Row({ row }: { row: ProjectEngagementRow }) {
  /*
    The expert's real name comes from ENG-5612 B3's `expertIdentity` — the
    expert is not a project member, so their id maps to nothing client-side
    (`parties.ts`). The neutral labels remain the fallback for rows with no
    expert yet (or no resolvable identity).

    The customer's own scope stays the *title* and the name sits on the meta
    line: the scope is what the reader scans first, and the period would not do
    the job — `startDate` is written at Make Offer, so it is null on exactly
    the pre-offer rows that need telling apart — while the status is already
    the badge.
  */
  const expert = row.expertName ?? (row.expertId === null ? 'Awaiting expert' : 'Expert');
  const hours = formatNteHours(row.nteHoursConsumed, row.nteHoursTotal);
  return (
    <Link
      to={`/engagements/${row.engagementId}`}
      className="flex flex-col gap-8 rounded-lg border border-surface-border bg-surface-card p-16 no-underline hover:border-brand-primary sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex flex-col gap-4">
        <span className="line-clamp-2 break-words text-on-surface subtitle-1">
          {row.requestScope ?? expert}
        </span>
        <span className="text-on-surface-muted body-2">
          {row.requestScope === null ? hours : `${expert} · ${hours}`}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-12">
        <span className="text-on-surface-muted body-2">
          Updated {formatDateShort(row.updatedAt)}
        </span>
        {/*
          The project page is a customer-side surface, so the badge reads the
          customer's side of the status copy.
        */}
        <StatusBadge status={row.status} awaiting={row.awaiting} viewerRole="work_approver" />
      </div>
    </Link>
  );
}
